import { Alert } from '@cmsgov/ds-medicare-gov'
import { Button } from 'components/Button/Button'
import LoadingButton from 'components/LoadingButton'
import { MyDialog } from 'components/MyDialog'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'
import { ConnectedAppsKeys } from 'events/lib/connectedApps'
import { useEvent } from 'hooks/useEvent'
import * as React from 'react'
import { Trans, useTranslation } from 'react-i18next'

export interface Props {
  connectedAppName: string
  onClose: () => void
  onConfirm: () => void | Promise<void>
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

export const connectedAppsStopModalId = 'medicare-my-connected-apps-stop-modal' as const
export type ConnectedAppModalId = typeof connectedAppsStopModalId

const getCloseElementId = (isSuccess: boolean, isError: boolean): ConnectedAppsKeys => {
  if (isError) {
    return 'accountsettings_connectedappsstopsharingerrorclose_click'
  } else if (isSuccess) {
    return 'accountsettings_connectedappsstopsharingsuccessclose_click'
  }

  return 'accountsettings_connectedappsstopsharingdecline_click'
}

export const ConnectedAppsStopModal: React.FC<Props> = ({
  onClose,
  onConfirm,
  connectedAppName,
  isLoading,
  isError,
  isSuccess
}) => {
  const { t } = useTranslation('connectedApps')

  const finished = isSuccess || isError
  const handleClose = useModalCloseEvent(
    'connectedApps',
    getCloseElementId(isSuccess, isError),
    onClose
  )
  const { fireEvent } = useEvent('connectedApps')

  return (
    <MyDialog
      heading={t('stopModal.heading')}
      containerId={connectedAppsStopModalId}
      closeButtonAriaLabel={t('stopModal.closeButtonAriaLabel')}
      onClose={handleClose}
    >
      <>
        {!finished && (
          <>
            <p>
              <Trans
                t={t}
                i18nKey='stopModal.confirmationText'
                values={{
                  connectedAppName
                }}
              />
            </p>
            <LoadingButton
              className="ds-c-button ds-c-button--solid my-u-py--6 my-u-mr--6"
              onClick={() => {
                fireEvent('accountsettings_connectedappsstopsharingconfirm_click')
                onConfirm()
              }}
              loading={isLoading}
            >
              {t('stopModal.buttons.confirm.label')}
            </LoadingButton>
            <Button
              className="ds-c-button my-u-py--6"
              // This close event is handled by the default modal close event
              onClick={handleClose}
            >
              {t('stopModal.buttons.cancel.label')}
            </Button>
          </>
        )}
        {isError && (
          (
            <Alert variation="warn" className="my-u-mb--14">
              {t('stopModal.errorMessage.content')}
            </Alert>
          )
        )}
        {isSuccess && (
          <Alert variation="success" hideIcon className="my-u-mb--14" heading={t('stopModal.successMessage.heading')}>
            <p>
              <Trans
                t={t}
                i18nKey='stopModal.successMessage.content'
                values={{
                  connectedAppName
                }}
              />
            </p>
          </Alert>
        )}
        {finished && (
          <Button
            className="ds-c-button ds-c-button--solid my-u-py--6 my-u-mr--6"
            // This close event is handled by the default modal close event
            onClick={handleClose}
          >
            {t('stopModal.buttons.close.label')}
          </Button>
        )}
      </>
    </MyDialog>
  )
}

export default ConnectedAppsStopModal
